/**
 * The contract between the community preview probe
 * (app/api/community-projects/[id]/preview-health) and the gallery modal that
 * consumes it (components/community/preview-modal.tsx). GT-02.
 *
 * Lives in lib/ rather than next to the route so the client component can import
 * the shape without reaching into a `server-only` module, and so the one
 * judgement that differs between the owner-facing and visitor-facing surfaces —
 * which verdicts should actually pull a preview — is written down once.
 */

import type {
  DeployedHealthReason,
  DeployedHealthVerdict,
} from "@/lib/preview/deployed-health";

/**
 * The verdict plus the absolute URL the client should frame. Sending the
 * normalized URL back means the browser never re-derives it (and can never
 * resolve a stored bare host relative to panda.new — see ./preview-url).
 */
export interface CommunityPreviewHealth extends DeployedHealthVerdict {
  url: string | null;
}

/**
 * Verdicts that mean "do not show this frame to a gallery visitor".
 *
 * Deliberately narrower than `!healthy`: only AFFIRMATIVE evidence the
 * deployment is not serving pulls the preview. Every other reason the classifier
 * can return — `redirect`, `protected`, `client_status`, `unreadable`,
 * `unreachable` — is ambiguous, and hiding a working preview on ambiguity would
 * trade one broken experience for another.
 *
 * `inactive` IS here, which is where the visitor's answer diverges from the
 * owner's: app/api/projects/[id]/deployed-preview-health reports an inactivated
 * app healthy so its owner sees the real state of their own deliberately-dark
 * app, but a gallery visitor has no context for that and would just be handed a
 * 404 from a detached subdomain.
 */
const VISITOR_BLOCKING_REASONS = new Set<DeployedHealthReason>([
  "http_not_found",
  "http_server_error",
  "error_page",
  "blank_body",
  "empty_document",
  "inactive",
  "not_published",
]);

/** True when this verdict must not be rendered to a gallery visitor. */
export function isVisitorBlockingReason(
  reason: DeployedHealthReason | string,
): boolean {
  return VISITOR_BLOCKING_REASONS.has(reason as DeployedHealthReason);
}
